import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ShieldCheck } from 'lucide-react';
import { siteData } from '../data.js';
import { cn } from '../lib/utils.js';

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileDropdownOpen, setIsMobileDropdownOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleLogoClick = (e) => {
    if (location.pathname === '/') {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const isScrolled = scrolled || !isHome;

  return (
    <nav className={cn(
      "fixed top-0 left-0 right-0 z-50 transition-all duration-300",
      isScrolled ? "bg-white/90 backdrop-blur-md shadow-sm py-3" : "bg-transparent py-5"
    )}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center">
          <Link to="/" className="flex items-center" onClick={handleLogoClick}>
            <img 
              src={siteData.logo} 
              alt={siteData.name} 
              className="h-[85px] w-auto object-contain hover:scale-110 transition-transform duration-300"
            />
          </Link>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-4 xl:gap-6">
            {siteData.navigation
              .filter((item) => item.href !== '/stoet-os' && item.href !== '/kontakt')
              .map((item) => {
                const isExternal = item.external || item.href.startsWith('http');
                const isActive = !isExternal && (location.pathname === item.href || 
                  (item.href === '/nyheder' && (location.pathname.startsWith('/nyheder') || location.pathname.startsWith('/events'))));
                
                const linkClasses = cn(
                  "text-sm font-medium transition-colors hover:text-red-600", 
                  isActive 
                    ? "text-red-600" 
                    : (isScrolled ? "text-slate-600" : "text-white/90")
                );

                if (isExternal) {
                  return (
                    <a
                      key={item.name}
                      href={item.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={linkClasses}
                      title="Tak For Din Indsats"
                    >
                      {item.name}
                    </a>
                  );
                }

                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={linkClasses}
                  >
                    {item.name}
                  </Link>
                );
              })}
            <div className="flex items-center gap-2.5">
              <Link
                to="/stoet-os"
                className="bg-red-600 text-white px-4 py-2 rounded-full text-sm font-semibold hover:bg-red-700 transition-colors shadow-md shadow-red-600/20 whitespace-nowrap"
              >
                Doner
              </Link>
              <a
                href="https://tilmeld.takfordinindsats.dk/signup"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-[#db9655] hover:bg-[#c98545] text-white px-4 py-2 rounded-full text-sm font-semibold transition-all whitespace-nowrap shadow-md shadow-[#db9655]/25"
              >
                Bliv Medlem
              </a>
            </div>
          </div>

          {/* Mobile toggle */}
          <button 
            className="lg:hidden p-2"
            onClick={() => setIsOpen(!isOpen)}
          >
            {isOpen ? <X className={isScrolled ? "text-slate-900" : "text-white"} /> : <Menu className={isScrolled ? "text-slate-900" : "text-white"} />}
          </button>
        </div>
      </div>

      {/* Mobile Nav */}
      {isOpen && (
        <div className="lg:hidden bg-white absolute top-full left-0 right-0 border-t border-slate-100 shadow-xl py-4 px-6 animate-in slide-in-from-top duration-300 max-h-[80vh] overflow-y-auto">
          <div className="flex flex-col gap-4">
            {siteData.navigation
              .filter((item) => item.href !== '/stoet-os' && item.href !== '/kontakt')
              .map((item) => {
                const isExternal = item.external || item.href.startsWith('http');
                const isActive = !isExternal && (location.pathname === item.href || 
                  (item.href === '/nyheder' && (location.pathname.startsWith('/nyheder') || location.pathname.startsWith('/events'))));

                const linkClasses = cn(
                  "text-base font-medium py-2 border-b border-slate-50 last:border-0",
                  isActive ? "text-red-600" : "text-slate-600"
                );

                if (isExternal) {
                  return (
                    <a
                      key={item.name}
                      href={item.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={linkClasses}
                      title="Tak For Din Indsats"
                      onClick={() => setIsOpen(false)}
                    >
                      {item.name}
                    </a>
                  );
                }

                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={linkClasses}
                    onClick={() => setIsOpen(false)}
                  >
                    {item.name}
                  </Link>
                );
              })}
            <div className="flex flex-col sm:flex-row gap-2.5 pt-2">
              <Link
                to="/stoet-os"
                className="bg-red-600 text-white px-5 py-3 rounded-xl text-center font-bold"
                onClick={() => setIsOpen(false)}
              >
                Doner
              </Link>
              <a
                href="https://tilmeld.takfordinindsats.dk/signup"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-[#db9655] hover:bg-[#c98545] text-white px-5 py-3 rounded-xl text-center font-bold transition-colors shadow-md shadow-[#db9655]/20"
                onClick={() => setIsOpen(false)}
              >
                Bliv Medlem
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}
